import { useState, useEffect, useCallback } from 'react'
import { useFactory } from '../../../context/FactoryContext'
import { sapConsumptionService } from '../../../services/sapConsumptionService'
import {
  useMaterialsConsumptionData,
  normalizeLineContext,
} from '../materials-consumption/MaterialesConsumos/hooks'
import {
  OrderDetailHeader,
  OrderSummaryAndRecipe,
  ConsumptionPeriodsManager,
} from '../materials-consumption/MaterialesConsumos/components/OrderDetail'

function formatDateTime(date) {
  if (!date) return 'No establecida'
  const d = new Date(date)
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1900) return 'No establecida'
  return d.toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function syncOrderDateTime(order, fieldNames) {
  for (const name of fieldNames) {
    const raw = order?.[name]
    if (!raw) continue
    const date = new Date(raw)
    if (!Number.isNaN(date.getTime()) && date.getFullYear() >= 1900) {
      return date
    }
  }
  return null
}

/**
 * Vista de detalle de una orden de fabricación enviada a SAP.
 * Reutiliza los componentes OrderDetail en modo solo lectura.
 */
function ManufacturingOrderDetailPanel({ selectedOrder, selectedLine, onBack }) {
  const { selectedFactory } = useFactory()
  const [activePeriod, setActivePeriod] = useState(null)
  const [expandedPeriodId, setExpandedPeriodId] = useState(null)
  const [expandedPeriodConsumptions, setExpandedPeriodConsumptions] = useState({})
  const [loadingExpandedPeriod, setLoadingExpandedPeriod] = useState(null)
  const [ofStartDateTime, setOfStartDateTime] = useState(null)
  const [ofEndDateTime, setOfEndDateTime] = useState(null)

  const {
    recipe,
    periods,
    derivedActivePeriod,
    consumptions,
    declaredQuantitiesByComponent,
    isLoadingRecipe,
    isLoadingPeriods,
    isLoadingConsumptions,
    refetchRecipe,
    refetchPeriods,
    refetchConsumptions,
  } = useMaterialsConsumptionData({
    selectedLine,
    selectedOrder,
    activePeriod,
    selectedFactory,
  })

  useEffect(() => {
    setActivePeriod(null)
    setExpandedPeriodId(null)
    setExpandedPeriodConsumptions({})
  }, [selectedOrder, selectedLine])

  useEffect(() => {
    if (!selectedOrder) {
      setOfStartDateTime(null)
      setOfEndDateTime(null)
      return
    }
    setOfStartDateTime(syncOrderDateTime(selectedOrder, ['StarteddAt', 'StartedAt', 'started_at', 'startedAt']))
    setOfEndDateTime(syncOrderDateTime(selectedOrder, ['FinishedAt', 'finished_at', 'finishedAt']))
  }, [selectedOrder])

  useEffect(() => {
    if (derivedActivePeriod && !activePeriod) {
      setActivePeriod(derivedActivePeriod)
    }
  }, [derivedActivePeriod, activePeriod])

  const loadPeriods = useCallback(async () => {
    const result = await refetchPeriods()
    const periodsData = result.data ?? []
    const active =
      periodsData.find((p) => p.IsActive === true || p.isActive === true || p.is_active === true) ??
      (periodsData.length > 0 ? periodsData[periodsData.length - 1] : null)
    setActivePeriod(active)
  }, [refetchPeriods])

  const loadPeriodConsumptions = useCallback(async (periodID) => {
    if (!periodID) return []
    try {
      setLoadingExpandedPeriod(periodID)
      const consumptionsData = await sapConsumptionService.getPeriodConsumptions(periodID)
      const filtered = consumptionsData.filter(
        (c) => c && (c.ComponentSapCode || c.componentSapCode)?.trim?.() !== ''
      )
      setExpandedPeriodConsumptions((prev) => ({
        ...prev,
        [periodID]: filtered,
      }))
      return filtered
    } catch {
      setExpandedPeriodConsumptions((prev) => ({
        ...prev,
        [periodID]: [],
      }))
      return []
    } finally {
      setLoadingExpandedPeriod(null)
    }
  }, [])

  const handleRefresh = useCallback(() => {
    refetchRecipe()
    refetchPeriods()
    refetchConsumptions()
  }, [refetchRecipe, refetchPeriods, refetchConsumptions])

  const lineForComponents = {
    ...selectedLine,
    line: selectedLine?.line || selectedLine?.code,
  }

  return (
    <>
      <OrderDetailHeader
        selectedOrder={selectedOrder}
        selectedLine={lineForComponents}
        ofStartDateTime={ofStartDateTime}
        ofEndDateTime={ofEndDateTime}
        formatDateTime={formatDateTime}
        loadingConsumptions={isLoadingConsumptions}
        loadingRecipe={isLoadingRecipe}
        onBack={onBack}
        onRefresh={handleRefresh}
        readOnly
      />

      <OrderSummaryAndRecipe
        selectedOrder={selectedOrder}
        recipe={recipe}
        consumptions={consumptions}
        declaredQuantitiesByComponent={declaredQuantitiesByComponent}
        loadingRecipe={isLoadingRecipe}
        readOnly
      />

      <ConsumptionPeriodsManager
        periods={periods}
        activePeriod={activePeriod}
        loadingPeriods={isLoadingPeriods}
        expandedPeriodId={expandedPeriodId}
        expandedPeriodConsumptions={expandedPeriodConsumptions}
        loadingExpandedPeriod={loadingExpandedPeriod}
        loadingConsumptions={isLoadingConsumptions}
        sendingToSAP={false}
        selectedOrder={selectedOrder}
        selectedLine={lineForComponents}
        recipe={recipe}
        declarationForm={{ componentSapCode: '', dosifierId: '', hopperId: '', quantity: '' }}
        setDeclarationForm={() => {}}
        dosifiersFromAPI={[]}
        hoppersFromAPI={[]}
        loadingDosifiersAPI={false}
        addingConsumption={false}
        onLoadPeriods={loadPeriods}
        onExpandPeriod={setExpandedPeriodId}
        onCollapsePeriod={() => setExpandedPeriodId(null)}
        onLoadPeriodConsumptions={loadPeriodConsumptions}
        getFactoryFromLine={(line) => normalizeLineContext(line, selectedFactory)?.factory ?? ''}
        readOnly
      />
    </>
  )
}

export default ManufacturingOrderDetailPanel
